/**
 * AUTHORITY — capabilities and scope, never a ladder.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THE RANK HELPER HAD TO GO
 * ═══════════════════════════════════════════════════════════════════════════
 * The multi-role foundation shipped `hasAtLeastAuthority(m, 'MANAGER')` backed
 * by a numeric ladder. A ladder is the wrong shape for this domain and it
 * would have been wrong in three specific, dangerous ways:
 *
 *   AUDITOR is READ-ONLY. On a ladder it sits above STAFF, which invites
 *   `>= AUDITOR` to read as "at least somewhat privileged" and hands an
 *   auditor a mutation they must never have. Auditors outrank nobody; they
 *   see more and change nothing.
 *
 *   REGION_MANAGER / DISTRICT_MANAGER are SCOPE-BOUND, not stronger managers.
 *   A district manager is not "a manager, but more". They are a manager
 *   somewhere specific, and a ladder silently drops the "somewhere".
 *
 *   ORG_ADMIN vs OWNER is not a magnitude either. They are different jobs.
 *
 * So authority is a SET OF CAPABILITIES plus a SCOPE, declared explicitly per
 * role. No arithmetic, no ordering, no "at least". A capability a role does
 * not list is a capability it does not have, and adding a role means writing
 * down what it may do rather than choosing where to slot it.
 *
 * WORK ROLES DO NOT APPEAR HERE AT ALL. That is the point: there is no
 * expression in this module that a work role could participate in, so
 * "technician gains manager rights" is not a bug that can be written.
 */

import type { AuthorityRole, MembershipRoleInput } from './multiRole';
import { resolveMembershipRoles } from './multiRole';

// ───────────────────────────────────────────────────────────────────────────
// Capabilities
// ───────────────────────────────────────────────────────────────────────────

/**
 * Administrative/access actions. Deliberately coarse and named for the ACT,
 * not for who happens to do it today.
 */
export type AuthorityCapability =
  /** See operational data for the shop. */
  | 'view_shop'
  /** See financial/compliance reporting. */
  | 'view_reports'
  /** Change shop configuration, hours, bays, settings. */
  | 'manage_shop_settings'
  /** Invite, remove, suspend people. */
  | 'manage_team'
  /** Change someone's AUTHORITY. The most dangerous capability here. */
  | 'manage_authority'
  /** Add or remove someone's operational WORK ROLES. */
  | 'manage_work_roles'
  /** Billing and subscription. */
  | 'manage_billing';

/** Where an authority applies. */
export type AuthorityScopeType = 'shop' | 'district' | 'region' | 'organization';

const NONE: readonly AuthorityCapability[] = [];

/**
 * The whole authorization model, written out.
 *
 * STAFF holds `view_shop` and nothing else — a technician can see the shop
 * they work in and administer none of it. Their ability to DO work comes from
 * work roles, which are a different module entirely.
 *
 * AUDITOR is the read-only role: it adds `view_reports` and stops. There is no
 * capability list in which AUDITOR appears alongside a `manage_*`.
 */
export const AUTHORITY_CAPABILITIES: Readonly<Record<AuthorityRole, readonly AuthorityCapability[]>> = {
  STAFF: ['view_shop'],

  // Read-only by construction. Sees more than STAFF, changes strictly less
  // than MANAGER — which is to say, nothing.
  AUDITOR: ['view_shop', 'view_reports'],

  MANAGER: [
    'view_shop', 'view_reports',
    'manage_shop_settings', 'manage_team', 'manage_work_roles',
    // NOT manage_authority: a manager staffs the shop, but does not mint
    // another manager or an owner.
    // NOT manage_billing.
  ],

  // Same capabilities as MANAGER. The difference is SCOPE, not power, and the
  // scope check below is what expresses it.
  DISTRICT_MANAGER: [
    'view_shop', 'view_reports',
    'manage_shop_settings', 'manage_team', 'manage_work_roles',
  ],
  REGION_MANAGER: [
    'view_shop', 'view_reports',
    'manage_shop_settings', 'manage_team', 'manage_work_roles',
  ],

  ORG_ADMIN: [
    'view_shop', 'view_reports',
    'manage_shop_settings', 'manage_team', 'manage_work_roles', 'manage_authority',
  ],

  OWNER: [
    'view_shop', 'view_reports',
    'manage_shop_settings', 'manage_team', 'manage_work_roles', 'manage_authority',
    'manage_billing',
  ],
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DELIBERATELY DEFERRED — declared above, and NOT enforceable at runtime
 * ═══════════════════════════════════════════════════════════════════════════
 * DISTRICT_MANAGER and REGION_MANAGER hold `manage_work_roles` in the table
 * above, and the runtime refuses them. That disagreement is resolved by
 * NARROWING THE CLAIM, not by widening the gate.
 *
 * Their authority is "a manager, over THIS district/region", and the schema
 * cannot express the second half: migration 20260802110000 constrains every
 * membership to `scope_type = 'shop' AND scope_id = shop_id` with zero
 * exceptions, and `shops` carries no district or region for a scope to point
 * at. `authorityReachesTarget` therefore fails them closed — correctly, and
 * that is the behaviour being kept.
 *
 * Admitting them on capability alone would grant district-wide power with
 * nothing bounding it, which is the exact failure the scope check exists to
 * prevent. So the capability table stays as the DECLARED model, and this
 * constant is the honest statement of what the runtime actually does.
 *
 * Mirrored by SCOPE_DEFERRED_AUTHORITIES in
 * supabase/functions/_shared/authorityCapabilities.ts, which is the
 * enforcement. Pinned by tests/unit/roles/enterpriseAuthorityNarrowing.test.ts.
 */
export const SCOPE_DEFERRED_AUTHORITIES: readonly AuthorityRole[] = [
  'DISTRICT_MANAGER', 'REGION_MANAGER',
];

/** The scope an authority is allowed to be bound to. */
export const AUTHORITY_NATURAL_SCOPE: Readonly<Record<AuthorityRole, AuthorityScopeType>> = {
  STAFF: 'shop',
  AUDITOR: 'shop',
  MANAGER: 'shop',
  DISTRICT_MANAGER: 'district',
  REGION_MANAGER: 'region',
  ORG_ADMIN: 'organization',
  OWNER: 'shop',
};

// ───────────────────────────────────────────────────────────────────────────
// Scope
// ───────────────────────────────────────────────────────────────────────────

export interface AuthorityScopeInput {
  /** `memberships.scope_type`. */
  scope_type?: string | null;
  /** `memberships.scope_id` — the region/district/org this authority covers. */
  scope_id?: string | null;
  /** The shop this membership row belongs to. */
  shop_id?: string | null;
}

/** Where the action is being attempted. */
export interface AuthorityTarget {
  shopId: string;
  /** The target shop's district, when known. */
  districtId?: string | null;
  /** The target shop's region, when known. */
  regionId?: string | null;
  /** The target shop's organization, when known. */
  organizationId?: string | null;
}

/**
 * Does this membership's authority reach the target?
 *
 * Scope-bound authorities must match the target's own district/region/org.
 * A district manager for district A has no authority in district B — that is
 * not a smaller amount of authority, it is none. Unknown target scope fails
 * CLOSED: if we cannot prove the shop is inside the scope, it is outside.
 */
export function authorityReachesTarget(
  authority: AuthorityRole,
  scope: AuthorityScopeInput | null | undefined,
  target: AuthorityTarget,
): boolean {
  const scopeType = (scope?.scope_type ?? 'shop') as AuthorityScopeType;
  const scopeId = scope?.scope_id ?? null;

  switch (AUTHORITY_NATURAL_SCOPE[authority]) {
    case 'shop':
      // Shop-bound: the membership's own shop and no other.
      return !!scope?.shop_id && scope.shop_id === target.shopId;

    case 'district':
      if (scopeType !== 'district' || !scopeId) return false;
      return !!target.districtId && target.districtId === scopeId;

    case 'region':
      if (scopeType !== 'region' || !scopeId) return false;
      return !!target.regionId && target.regionId === scopeId;

    case 'organization':
      if (scopeType !== 'organization' || !scopeId) return false;
      return !!target.organizationId && target.organizationId === scopeId;
  }
}

// ───────────────────────────────────────────────────────────────────────────
// The question consumers should ask
// ───────────────────────────────────────────────────────────────────────────

export interface AuthorityCheckInput extends MembershipRoleInput, AuthorityScopeInput {}

/** Does this membership hold the capability at all, ignoring where? */
export function hasCapability(
  m: AuthorityCheckInput | null | undefined,
  capability: AuthorityCapability,
): boolean {
  const { authority } = resolveMembershipRoles(m);
  return (AUTHORITY_CAPABILITIES[authority] ?? NONE).includes(capability);
}

/**
 * The real check: the capability AND the reach.
 *
 * Both halves are required. Holding `manage_team` is meaningless against a
 * shop you have no authority over, and being in scope is meaningless without
 * the capability.
 */
export function canPerformAuthorityAction(
  m: AuthorityCheckInput | null | undefined,
  capability: AuthorityCapability,
  target: AuthorityTarget,
): boolean {
  if (!m) return false;
  const { authority } = resolveMembershipRoles(m);
  if (!(AUTHORITY_CAPABILITIES[authority] ?? NONE).includes(capability)) return false;
  return authorityReachesTarget(authority, m, target);
}

/**
 * May the actor set someone's authority to `next`?
 *
 * Two independent rules, and both must hold:
 *   1. the actor holds `manage_authority` within the target shop;
 *   2. the actor may not grant a capability they do not themselves hold.
 *
 * (2) is what stops privilege escalation by proxy — a MANAGER cannot mint an
 * OWNER, and nobody can hand out `manage_billing` unless they have it.
 */
export function canAssignAuthority(
  actor: AuthorityCheckInput | null | undefined,
  next: AuthorityRole,
  target: AuthorityTarget,
): boolean {
  if (!canPerformAuthorityAction(actor, 'manage_authority', target)) return false;

  const actorAuthority = resolveMembershipRoles(actor).authority;
  const actorCaps = new Set(AUTHORITY_CAPABILITIES[actorAuthority] ?? NONE);
  return (AUTHORITY_CAPABILITIES[next] ?? NONE).every((c) => actorCaps.has(c));
}

/**
 * May the actor create an INVITATION offering `next` authority?
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS IS NOT `canAssignAuthority`
 * ═══════════════════════════════════════════════════════════════════════════
 * Every invitation must name an authority, because the membership it creates
 * needs one — there is no "leave it alone" for somebody who is not there yet.
 * Requiring `manage_authority` for all of them would stop a MANAGER inviting a
 * technician, which is the ordinary weekly staffing operation.
 *
 * So STAFF — the authority floor, `view_shop` and nothing else — is what
 * `manage_team` alone may offer. Offering it is staffing, not delegation.
 * ANYTHING ABOVE STAFF is delegation and takes the full doctrine.
 *
 * This mirrors `canInviteWithAuthority` in
 * supabase/functions/_shared/authorityCapabilities.ts, which is the
 * enforcement. This copy exists so the invitation form can avoid OFFERING an
 * authority that would bounce — it decides nothing.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY REACH IS CHECKED HERE AND NOT VIA `authorityReachesTarget`
 * ═══════════════════════════════════════════════════════════════════════════
 * The server establishes reach by RESOLVING THE ACTOR'S MEMBERSHIP IN THE
 * TARGET SHOP — `.eq('user_id', …).eq('shop_id', shopId).eq('status','active')`
 * — so an actor with no membership there simply is not found and there is
 * nothing to invite into. This mirror asks the same question, so the form
 * offers exactly what the server allows.
 *
 * It deliberately does NOT route through `authorityReachesTarget`, whose
 * `AUTHORITY_NATURAL_SCOPE` maps ORG_ADMIN to 'organization' and would
 * therefore refuse every ORG_ADMIN — because migration 20260802110000
 * constrains every membership to `scope_type = 'shop'`, so an
 * organization-scoped membership cannot exist to satisfy it. ORG_ADMIN is an
 * authority the runtime genuinely honours today (it is in
 * `ROLES_DESTRUCTIVE`), and quietly refusing it here would be a behaviour
 * regression dressed as a scope check rather than the narrowing Audit 8 asks
 * for. The aspirational org/district/region scope model is recorded as an open
 * edge, not silently enforced by the invitation form.
 */
export function canInviteWithAuthority(
  actor: AuthorityCheckInput | null | undefined,
  next: AuthorityRole,
  target: AuthorityTarget,
): boolean {
  if (!actor) return false;
  // Deferred scope — see SCOPE_DEFERRED_AUTHORITIES. Offering an authority
  // whose boundary is unrepresentable mints power nothing can bound, and an
  // actor holding one has no boundary to act within. Both are refused.
  const actorAuthority = resolveMembershipRoles(actor).authority;
  if (SCOPE_DEFERRED_AUTHORITIES.includes(next)) return false;
  if (SCOPE_DEFERRED_AUTHORITIES.includes(actorAuthority)) return false;

  // Reach: the actor's membership must be in the shop being invited into.
  if (!actor.shop_id || actor.shop_id !== target.shopId) return false;

  const actorCaps = new Set(AUTHORITY_CAPABILITIES[actorAuthority] ?? NONE);
  if (!actorCaps.has('manage_team')) return false;
  if (next === 'STAFF') return true;
  if (!actorCaps.has('manage_authority')) return false;

  return (AUTHORITY_CAPABILITIES[next] ?? NONE).every((c) => actorCaps.has(c));
}

/**
 * MAY THIS PERSON ADMINISTER THE TEAM — the question the UI gate must ask.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 * Team Management gated itself on the LEGACY SCALAR:
 *
 *     const isManager = membership?.role === 'MANAGER' || membership?.role === 'OWNER';
 *
 * while the server admits `ROLES_DESTRUCTIVE` — OWNER, MANAGER and ORG_ADMIN.
 * So an ORG_ADMIN was refused the page by the client and accepted by every
 * endpoint behind it: two layers answering the same question differently, with
 * the UI as the only thing standing in the way.
 *
 * This is the one expression of that answer, and it is built from the model
 * rather than from a list of strings:
 *
 *     holds `manage_team`                  MANAGER, DISTRICT_MANAGER,
 *                                          REGION_MANAGER, ORG_ADMIN, OWNER
 *     minus the scope-deferred authorities  DISTRICT_MANAGER, REGION_MANAGER
 *     ------------------------------------------------------------------
 *     = OWNER, MANAGER, ORG_ADMIN           exactly ROLES_DESTRUCTIVE
 *
 * `tests/unit/roles/teamAdministrationParity.test.ts` asserts that equality
 * against the guard's own source, so the two cannot drift apart again.
 */
export function canAdministerTeam(
  m: AuthorityCheckInput | null | undefined,
): boolean {
  if (!m) return false;
  const { authority } = resolveMembershipRoles(m);
  if (SCOPE_DEFERRED_AUTHORITIES.includes(authority)) return false;
  return (AUTHORITY_CAPABILITIES[authority] ?? NONE).includes('manage_team');
}

/**
 * The authorities an actor may actually SET on an existing member.
 *
 * Capability subset alone was not enough: DISTRICT_MANAGER and REGION_MANAGER
 * pass it (their capabilities are a subset of an owner's) and the server
 * refuses them outright with `authority_scope_unsupported`, because their
 * scope is unrepresentable. The member editor was therefore offering an owner
 * two choices that could only ever bounce.
 */
export function assignableAuthoritiesFor(
  actorAuthority: AuthorityRole,
): AuthorityRole[] {
  const actorCaps = new Set(AUTHORITY_CAPABILITIES[actorAuthority] ?? NONE);
  return (Object.keys(AUTHORITY_CAPABILITIES) as AuthorityRole[]).filter((r) =>
    !SCOPE_DEFERRED_AUTHORITIES.includes(r) &&
    (AUTHORITY_CAPABILITIES[r] ?? NONE).every((c) => actorCaps.has(c)));
}

/**
 * Self-elevation is refused outright, independently of capability.
 *
 * Even an OWNER editing their own row cannot RAISE it — there is no legitimate
 * flow where a person grants themselves something they did not already have,
 * and refusing it here means no caller has to remember to.
 */
export function isSelfElevation(
  actorUserId: string | null | undefined,
  targetUserId: string | null | undefined,
  currentAuthority: AuthorityRole,
  nextAuthority: AuthorityRole,
): boolean {
  if (!actorUserId || actorUserId !== targetUserId) return false;
  if (currentAuthority === nextAuthority) return false;

  const current = new Set(AUTHORITY_CAPABILITIES[currentAuthority] ?? NONE);
  // Gaining ANY capability you did not already hold is elevation.
  return (AUTHORITY_CAPABILITIES[nextAuthority] ?? NONE).some((c) => !current.has(c));
}
